import {noop} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useMemo} from 'react';
import {Node, XYPosition} from 'reactflow';

import isArchived from 'archive/isArchived';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {createTaskNode} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/nodeUtils';
import TaskSchemaNodeRenderer from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/TaskSchemaNodeRenderer';

import {generateTaskSchemaNodeIdentifier} from '../flowchartIdentifierUtils';
import {ProcessFlowchartNodeType} from '../processFlowchartConfigEditorConstants';
import {TaskSchemaNodeData, TaskSchemaStaticNodeData} from '../processFlowchartConfigEditorNodeTypes';
import {
  createStaticNodeDataFromTaskDefinitionVersion,
  FlowchartOptions,
} from '../processFlowchartConfigEditorUtils';
import {ConfigurableTaskDefinitionGQL} from '../useAllSchemasById';
import {NodeSidepanelSectionProps} from './NodeSidepanelSection';
import {DEFAULT_NODE_PROPS, DraggableNode} from './sidepanelNodeUtils';

/**
 * Creates a node specifically for the sidepanel.
 * This node is draggable onto the canvas, but does not have sections or field / header interactions.
 */
function createNodeDataForSidepanel(nodeStaticData: TaskSchemaStaticNodeData): TaskSchemaNodeData {
  return {
    ...nodeStaticData,
    sections: [],
    hideSectionSummaries: true,
    onNodeHeaderClick: noop,
    onFieldClick: noop,
    nodeDetailsSidepanelContent: [],
    rowRenderer: () => null,
    nodeHeaderHandleRenderer: () => null,
  };
}

function DraggableTaskNode(props: {
  taskDefinition: ConfigurableTaskDefinitionGQL;
  nodeId: string;
  addNode: (node: Node<TaskSchemaNodeData>) => void;
  options: FlowchartOptions;
}): JSX.Element {
  const staticNodeData = useMemo(
    () =>
      createStaticNodeDataFromTaskDefinitionVersion(
        props.nodeId,
        props.taskDefinition.latestVersion,
        props.taskDefinition.latestVersion.processTaskSchema.id,
        ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
        [props.taskDefinition.latestVersion.processTaskSchema]
      ),
    [props.nodeId, props.taskDefinition.latestVersion]
  );

  const addTaskNode = useCallback(
    (position: XYPosition) => {
      const newNode = createTaskNode(staticNodeData, position, props.options);
      props.addNode(newNode);
    },
    [props, staticNodeData]
  );

  const nodeData = createNodeDataForSidepanel(staticNodeData);

  const nodeRenderer = useCallback(
    ({dragging}: {dragging: boolean}) => (
      <TaskSchemaNodeRenderer
        {...DEFAULT_NODE_PROPS}
        id={props.nodeId}
        type={ProcessFlowchartNodeType.TASK_SCHEMA_NODE}
        data={nodeData}
        dragging={dragging}
      />
    ),
    [props.nodeId, nodeData]
  );

  return <DraggableNode addNode={addTaskNode} nodeRenderer={nodeRenderer} />;
}

/**
 * A sidepanel component that displays all available task nodes that can be added to the flowchart
 */
export default function TaskNodeSidepanelSection(
  props: Omit<NodeSidepanelSectionProps, 'section' | 'nodeTypes'>
): JSX.Element {
  return (
    <div className={css(styles.taskNodesContainer)}>
      {props.taskDefinitionsForSidepanel
        // Task definitions are not yet archivable so we instead filter out task definitions whose latest version
        // is associated with an archived schema.
        .filter((definition) => !isArchived(definition.latestVersion.processTaskSchema))
        .filter((definition) => {
          if (props.searchQuery == null) {
            return true;
          }
          return definition.latestVersion.processTaskSchema.name
            .toLowerCase()
            .includes(props.searchQuery.toLowerCase());
        })
        .map((definition) => {
          const nodeId = generateTaskSchemaNodeIdentifier(definition.latestVersion.processTaskSchema);
          return (
            <DraggableTaskNode
              key={nodeId}
              nodeId={nodeId}
              taskDefinition={definition}
              addNode={props.addNodeToCanvas}
              options={props.options}
            />
          );
        })}
    </div>
  );
}

const styles = StyleSheet.create({
  taskNodesContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
});
